/*!
  Pioneer WFRIA MessageBar v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["MessageBar"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("MessageBar") < 0) {
    window.__PIONEER_GLOBAL__.components.push("MessageBar");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "className", "container", "icon", "open", "style", "zIndex", "role", "closeButton", "closeButtonLocation", "theme", "customStrings", "elementToFocusOnClose", "onClose", "messageBarStyles", "aria-labelledby"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import throttle from "lodash.throttle";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { CSSTransition } from "react-transition-group";
import { KEYBOARD_KEYS, ZINDEX } from "../common/constants";
import { useOnEscape, useUniqueId } from "../common/hooks";
import { requiredIf, validIcon } from "../common/proptypes";
import { getFocusableElements, safeMerge } from "../common/utilities";
import { PanelContext } from "../Panel/PanelContext";
import { ThemeProviderContext } from "../ThemeProvider/ThemeProviderContext";
import { MESSAGEBAR_ICON_CLASS } from "../themes/common/classes";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { Button, IconClose } from "..";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const FADEOUT_DURATION = 2000;
const SCROLL_THROTTLE_TIME = 150;
const MESSAGEBAR_PORTAL = "pnr-message-bar-portal";
const MESSAGEBAR_STYLES = process.env.NODE_ENV !== "production" ? ["confirmation", "error", "information", "informational", "success", "utility", "warning"] : {};
/**
 * MessageBar is a React component that allows you to display
 * messages in a ribbon at the top of the page.
 * MessageBar combines text, icons, and center aligns
 * the content to provide contextual information.
 * It will disappear in 2 seconds after user interacts with a page by scrolling,
 * clicking or typing on the keyboard.
 */

function MessageBar(_ref) {
  let {
    children,
    className,
    container,
    // eslint-disable-line no-unused-vars
    icon,
    open,
    style,
    zIndex,
    role,
    closeButton,
    closeButtonLocation: closeButtonLocationProp,
    theme,
    customStrings,
    // eslint-disable-line no-unused-vars
    elementToFocusOnClose,
    // eslint-disable-line no-unused-vars
    onClose,
    messageBarStyles,
    "aria-labelledby": ariaLabelledBy
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const internalTheme = useTheme();
  let closeButtonLocation = closeButtonLocationProp;

  if (closeButton) {
    // Check if custom button is being used.
    if ( /*#__PURE__*/React.isValidElement(closeButton)) {
      if (!closeButtonLocation) {
        closeButtonLocation = "footer";
      }
    } else {
      closeButtonLocation = "header";
    }
  }

  const showCloseButtonInHeader = closeButton && closeButtonLocation === "header";
  const showCloseButtonInFooter = closeButton && closeButtonLocation === "footer"; // Get the PanelContext when rendering a MessageBar within a Panel.

  const panelContext = useContext(PanelContext); // If the MessageBar is in a Panel, get the Panel type ("fullScreen", "modal", etc).

  const messageBarInModalType = panelContext.type || null;
  const {
    cssRoot,
    messagebarRow,
    headerStyles,
    messagebarIconContainer,
    messagebarMessages,
    messagesContainer,
    footerStyles
  } = extractTheme({
    internalTheme,
    theme,
    messageBarInModalType,
    showCloseButtonInFooter,
    showCloseButtonInHeader
  });
  const uniqueID = useUniqueId();

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const messageBarContainerNode = useRef(document.createElement("div"));
  const messageBarRootNode = useRef(null);
  const closeButtonRef = useRef();
  const messageTextRef = useRef();
  const messageBarContainerRef = useRef();
  const focusableElementsInMessageBar = useRef(null);
  const [textRole, setTextRole] = useState(null);
  const context = useContext(ThemeProviderContext) || {};
  const wrapperClassNames = clsx("pnr-message-bar", context.scopedClassName, className, {
    "pnr-message-bar__header--show-close-button": showCloseButtonInFooter
  });
  useEffect(() => {
    const containerNode = container();
    messageBarRootNode.current = containerNode.querySelector(`.${MESSAGEBAR_PORTAL}`);
    messageBarRootNode.current && (messageBarRootNode.current.innerHtml = "");

    if (!messageBarRootNode.current) {
      const messageBarRootDiv = document.createElement("div");
      messageBarRootDiv.setAttribute("class", MESSAGEBAR_PORTAL);
      messageBarRootNode.current = containerNode.insertBefore(messageBarRootDiv, containerNode.firstChild);
    }

    messageBarRootNode.current.appendChild(messageBarContainerNode.current); // Force MessageBar's `aria-hidden` to `false`, though a peer component like Panel sets it to `true`.

    messageBarRootNode.current.setAttribute("aria-hidden", false);
  }, [container, open]);
  /**
   * handleClose method triggers the configured handler for document click, page scroll and
   * key down. Generally end user uses it for hiding the messageBar.
   */

  const handleClose = e => {
    open;

    if (closeButton) {
      const elementToFocus = typeof elementToFocusOnClose === "string" ? document.querySelector(elementToFocusOnClose) : elementToFocusOnClose();
      elementToFocus && elementToFocus.focus();
    }

    onClose && onClose(e);
  };
  /**
   * handleDocumentKeyDown method triggers the configured handler on keydown
   * event. Except for space and enter keys as document click handler will be triggering for those keys.
   * Stops event propagation on close so that a containing component such as Panel won't close as well.
   */


  const handleDocumentKeyDown = e => {
    const {
      key
    } = e;

    if (key !== KEYBOARD_KEYS.SPACE && key !== KEYBOARD_KEYS.ENTER) {
      handleClose(e);
    }
  };

  const handleWindowScrollThrottled = throttle(handleClose, SCROLL_THROTTLE_TIME);

  const addListeners = () => {
    document.addEventListener("click", handleClose, true);
    document.addEventListener("keydown", handleDocumentKeyDown);
    document.addEventListener("scroll", handleWindowScrollThrottled);
  };
  /**
   * Removing the click, scroll and keydown handlers.
   */


  const removeListeners = () => {
    document.removeEventListener("click", handleClose, true);
    document.removeEventListener("keydown", handleDocumentKeyDown);
    handleWindowScrollThrottled && handleWindowScrollThrottled.cancel();
    document.removeEventListener("scroll", handleWindowScrollThrottled);
  };

  const setButtonRef = closeButton => {
    closeButtonRef.current = closeButton;
  };
  /**
   * Generates the close button to be used for the manually dismissible MessageBar.
   * @param {Node} closeButton The node of the close button element.
   */


  const getCloseButtonNode = closeButton => {
    // If the user passed in a valid node use that for the close button.
    return /*#__PURE__*/React.isValidElement(closeButton) ? /*#__PURE__*/React.cloneElement(closeButton, {
      ref: setButtonRef
    }) : _jsx(Button, {
      className: "pnr-message-bar__close-button wf-tooltip--bottom-right",
      kind: "chromeless",
      ref: closeButtonRef,
      "aria-label": componentStrings.closeButtonAriaLabel,
      centerIcon: _jsx(IconClose, {
        decorative: true,
        size: "xsmall"
      }),
      onClick: handleClose
    });
  };
  /**
   * @description Dismisses the MessageBar on ESC and sets focus to trigger element.
   * Stops event propagation on close so that a containing component such as Panel won't close as well.
   * @param {Event} e The browser event.
   */


  const handleCustomCloseKeyDown = e => {
    const {
      key,
      shiftKey,
      target
    } = e;

    if (key === KEYBOARD_KEYS.TAB) {
      const focusableElementsLength = focusableElementsInMessageBar.current.length;
      const firstFocusableElement = focusableElementsLength > 0 && focusableElementsInMessageBar.current[0];
      /**
       * There is only one focusable item in the Message Bar, so in order to
       * avoid a perceived keyboard trap, focus is toggled between the close
       * button and the Message Bar container. If more than one focusable item,
       * so normal cycle rules apply.
       */

      if (focusableElementsLength === 1) {
        e.preventDefault();
        const messageTextNode = messageTextRef && messageTextRef.current;

        if (target === firstFocusableElement) {
          messageTextNode && messageTextNode.focus();
        } else {
          firstFocusableElement && firstFocusableElement.focus();
        }
      } else if (shiftKey && target === firstFocusableElement) {
        e.preventDefault();
        focusableElementsInMessageBar.current[focusableElementsLength - 1].focus();
      } else if (!shiftKey && target === focusableElementsInMessageBar.current[focusableElementsLength - 1]) {
        e.preventDefault();
        firstFocusableElement && firstFocusableElement.focus();
      }
    }
  };

  const handleFocusOnMessage = e => {
    messageTextRef && e.target === messageTextRef.current && messageBarContainerRef.current.classList.add("pnr-message-bar-container--focus");
  };

  const handleBlurOnMessage = e => {
    messageTextRef && e.target === messageTextRef.current && messageBarContainerRef.current.classList.remove("pnr-message-bar-container--focus");
  };

  useEffect(() => {
    // Force MessageBar's `aria-hidden` to `false`, though a peer component like Panel sets it `true`.
    messageBarRootNode.current.setAttribute("aria-hidden", false);
    const messageBarContainer = messageBarContainerNode.current;

    if (!closeButton) {
      open ? addListeners() : removeListeners();
    } else if (open) {
      // Focus on the close button when it renders.
      closeButtonRef.current.focus();
      focusableElementsInMessageBar.current = getFocusableElements(messageBarContainer);
      setTextRole(focusableElementsInMessageBar.current.length > 1 ? null : "text");
      document.addEventListener("keydown", handleCustomCloseKeyDown);
    } else {
      focusableElementsInMessageBar.current = null;
      setTextRole(null);
      document.removeEventListener("keydown", handleCustomCloseKeyDown);
    }

    return () => {
      messageBarRootNode.current.removeAttribute("role");
      removeListeners();
      document.removeEventListener("keydown", handleCustomCloseKeyDown);
      messageBarRootNode.current.remove(messageBarContainer);
    };
  }, [closeButton, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeButtonNode = closeButton && getCloseButtonNode(closeButton);
  const ariaRole = closeButton ? "dialog" : role;
  const messageBarTextId = `pnr-message-bar__text-${uniqueID}`;
  const messageBarIconId = icon ? `pnr-message-bar__icon-${uniqueID}` : null;
  const combinedStyles = safeMerge(cssRoot, messageBarStyles);
  const messageAnnouncement = ariaLabelledBy || `${messageBarIconId} ${messageBarTextId}`;

  const elementStyles = _objectSpread(_objectSpread({}, style), {}, {
    zIndex
  });

  useOnEscape(handleClose, open);
  return /*#__PURE__*/ReactDOM.createPortal(_jsx(CSSTransition, {
    in: open,
    timeout: FADEOUT_DURATION,
    classNames: "pnr-message-bar-animation",
    mountOnEnter: true,
    unmountOnExit: true,
    children: _jsxs("div", _objectSpread(_objectSpread({
      css: combinedStyles,
      style: elementStyles
    }, otherProps), {}, {
      className: wrapperClassNames,
      role: open ? ariaRole : null,
      "aria-labelledby": closeButton ? messageAnnouncement : null,
      ref: messageBarContainerRef,
      children: [showCloseButtonInHeader && _jsx("div", {
        css: headerStyles,
        className: "pnr-message-bar__header",
        children: closeButtonNode
      }), _jsxs("div", {
        css: messagebarRow,
        children: [icon && _jsx("div", {
          id: messageBarIconId,
          css: messagebarIconContainer,
          className: MESSAGEBAR_ICON_CLASS,
          children: /*#__PURE__*/React.cloneElement(icon, {
            size: "medium"
          })
        }), closeButton ? _jsxs("div", {
          css: messagebarMessages,
          children: [_jsx("div", {
            // eslint-disable-line jsx-a11y/no-static-element-interactions
            css: messagesContainer,
            ref: messageTextRef,
            tabIndex: -1,
            "aria-describedby": messageAnnouncement,
            role: textRole,
            onFocus: handleFocusOnMessage,
            onBlur: handleBlurOnMessage,
            children: _jsx("span", {
              className: "pnr-message-bar__text",
              id: messageBarTextId,
              children: children
            })
          }), showCloseButtonInFooter && _jsx("div", {
            css: footerStyles,
            className: "pnr-message-bar__footer",
            children: closeButtonNode
          })]
        }) : _jsx("div", {
          css: messagebarMessages,
          children: children
        })]
      })]
    }))
  }), messageBarContainerNode.current);
}

MessageBar.propTypes = process.env.NODE_ENV !== "production" ? {
  /** This is what will be displayed inside the MessageBar. */
  children: PropTypes.node.isRequired,

  /** aria-labelledby for the MessageBar. */
  ["aria-labelledby"]: PropTypes.string,

  /** The CSS class name of the root element. */
  className: PropTypes.string,

  /**
   * This prop produces a button control that closes the MessageBox. If it is
   * set to `true`, then a default button will be placed in the Header. If it
   * is a Node, then the custom Button can be placed in the Header or the Footer
   * of the MessageBar depending on the value of the `closeButtonLocation` prop.
   */
  closeButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),

  /**
   * Determines the placement location of the custom close button
   * that is provided as a Node to the `closeButton` prop.
   * It defaults to `footer` but can be set to `header`.
   * This prop is not applicable to the default close Button.
   */
  closeButtonLocation: PropTypes.oneOf(["header", "footer"]),

  /**
   * A function which returns a container node. The MessageBar portal will be
   * added as the first child of the container node.
   * Defaults to a function that returns `document.body` if not provided.
   */
  container: PropTypes.func,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * Defaults to "Close message". Used to override the text for
     * the aria-label of the close button.
     */
    closeButtonAriaLabel: PropTypes.string
  }),

  /**
   * This prop identifies the element that should receive focus after the
   * MessageBar is closed. It can be a css selector or a function that returns
   * a DOM node. It is required if the `closeButton` prop is set to `true`.
   */
  elementToFocusOnClose: requiredIf(PropTypes.oneOfType([PropTypes.string, PropTypes.func]), (props, propName) => !props[propName] && props.closeButton === true, "if the `closeButton` prop is set to true"),

  /** A React component of type Icon. See Iconography section. */
  icon: validIcon,

  /**
   * MessageBar content style (CSS-in-JS).
   */
  messageBarStyles: PropTypes.shape(),

  /** Handler to be called on document click, page scroll  and on key down. */
  onClose: PropTypes.func,

  /** prop to show or hide MessageBar. */
  open: PropTypes.bool,

  /** The role of the MessageBar. */
  role: PropTypes.string,

  /** The style of the MessageBar element. */
  style: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Sets the background-color of MessageBar according to prop value passed.
   */
  theme: PropTypes.oneOf(MESSAGEBAR_STYLES),

  /** The zIndex to be set on the MessageBar in relation with other elements on the page. */
  zIndex: PropTypes.number
} : {};
MessageBar.defaultProps = {
  children: null,
  "aria-labelledby": null,
  className: "",
  closeButton: null,
  closeButtonLocation: null,
  container: () => document.body,
  customStrings: null,
  elementToFocusOnClose: null,
  icon: null,
  messageBarStyles: {},
  onClose: null,
  open: false,
  role: "alert",
  style: null,
  theme: "information",
  zIndex: ZINDEX.MESSAGE_BAR
};
export default MessageBar;